import { createContext, useContext, useEffect, useState } from "react";
import apiRequest, { ApiResponse } from "../axios/useApi";
import { formatDate, handleApiError } from "../lib/utils";
import { UIContext } from "./UIProvider";
import { BankingContext } from "./BankingProvider";
import {
  CreditMemo,
  FiltersInterface,
  Invoice,
  InvoiceFilterByInterface,
} from "../pages/Banking/BankingInterface";
import {
  MatchTransactionApi,
  MatchTransactionPayload,
} from "../pages/Banking/components/BankingApis";

export interface CreditMemoContextType {
  invoices: Invoice[];
  isLoading: {
    isMemoLoading: boolean;
    isInvoiceLoading: boolean;
    isMatchLoading: boolean;
  };
  pagination: {
    pageSize: number;
    pageNumber: number;
    totalPages: number;
    totalRecords: number;
  };
  creditMemoPagiation: {
    pageSize: number;
    pageNumber: number;
    totalPages: number;
    totalRecords: number;
  };
  memofilters: FiltersInterface;
  transactionfilters: FiltersInterface;
  handlePageChange: (type: "invoice" | "memo", page: number) => void;
  handlePageSizeChange: (type: "invoice" | "memo", size: number) => void;
  filterByOptions: {
    invoiceOptions: { label: string; value: string }[];
    memoOptions: { label: string; value: string }[];
  };
  handleFilterChange: (
    type: "invoice" | "credit",
    field: string,
    value: any
  ) => void;
  setTransactionFilters: React.Dispatch<
    React.SetStateAction<{
      startDate: Date | null;
      endDate: Date | null;
      term: string;
      filterBy: any;
    }>
  >;
  fetchFilterByList: () => void;
  rowSelection: { [key: string]: boolean };
  setRowSelection: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  creditMemoRowSelection: { [key: string]: boolean };
  setCreditMemoRowSelection: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  payments: { [key: string]: any };
  handlePaymentChange: (
    type: "invoice" | "memo",
    invoiceId: string,
    value: string,
    totalAmt: number
  ) => void;
  creditMemos: CreditMemo[];
  creditMemoPayments: { [key: string]: any };
  handleMatch: () => void;
  error: string | null;
  handleCloseDialog: () => void;
  getPaymentError: (value: string, totalAmt: number) => string | undefined;
}

export const CreditMemoContext = createContext<CreditMemoContextType>({
  invoices: [],
  isLoading: {
    isMemoLoading: false,
    isInvoiceLoading: false,
    isMatchLoading: false,
  },
  pagination: {
    pageSize: 10,
    pageNumber: 1,
    totalPages: 1,
    totalRecords: 0,
  },
  creditMemoPagiation: {
    pageSize: 10,
    pageNumber: 1,
    totalPages: 1,
    totalRecords: 0,
  },
  memofilters: {
    startDate: null,
    endDate: null,
    term: "",
    filterBy: "",
  },
  transactionfilters: {
    startDate: null,
    endDate: null,
    term: "",
    filterBy: "",
  },
  handlePageChange: () => {},
  handlePageSizeChange: () => {},
  filterByOptions: {
    invoiceOptions: [],
    memoOptions: [],
  },
  handleFilterChange: () => {},
  fetchFilterByList: () => {},
  rowSelection: {},
  setRowSelection: () => {},
  creditMemoRowSelection: {},
  setCreditMemoRowSelection: () => {},
  payments: {},
  handlePaymentChange: () => {},
  creditMemos: [],
  creditMemoPayments: {},
  handleMatch: () => {},
  error: null,
  handleCloseDialog: () => {},
  getPaymentError: () => {},
});

export const CreditMemoContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { onError, onSuccess, businessId } = useContext(UIContext);
  const {
    modalStates,
    editTransaction,
    fetchTransactions,
    getCategoriesCount,
    handleMatchTransaction,
  } = useContext(BankingContext);

  // Helper to get start of current month
  const getMonthStart = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const getMonthEnd = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0); // Day 0 of next month = last day of current month
  };

  const [rowSelection, setRowSelection] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [creditMemoRowSelection, setCreditMemoRowSelection] = useState<{
    [key: string]: boolean;
  }>({});

  const [isLoading, setIsLoading] = useState({
    isInvoiceLoading: false,
    isMemoLoading: false,
    isMatchLoading: false,
  });
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [creditMemos, setCreditMemos] = useState<CreditMemo[]>([]);

  const [filterByOptions, setFilterByOptions] = useState<{
    invoiceOptions: { label: string; value: string }[];
    memoOptions: { label: string; value: string }[];
  }>({
    invoiceOptions: [] as { label: string; value: string }[],
    memoOptions: [] as { label: string; value: string }[],
  });
  const [error, setError] = useState<string | null>(null);

  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageNumber: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const [creditMemoPagiation, setCreditMemoPagiation] = useState({
    pageSize: 10,
    pageNumber: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const [transactionfilters, setTransactionFilters] = useState<{
    startDate: Date | null;
    endDate: Date | null;
    term: string;
    filterBy: any; // or a more specific type if you know your typeOptions structure
  }>({
    startDate: getMonthStart(),
    endDate: getMonthEnd(),
    term: "",
    filterBy: null,
  });

  const [memofilters, setMemoFilters] = useState<{
    startDate: Date | null;
    endDate: Date | null;
    term: string;
    filterBy: any; // or a more specific type if you know your typeOptions structure
  }>({
    startDate: getMonthStart(),
    endDate: getMonthEnd(),
    term: "",
    filterBy: 58,
  });

  interface PaymentValue {
    value: string;
    totalAmt: number;
  }

  const [payments, setPayments] = useState<{ [key: string]: PaymentValue }>({});
  const [creditMemoPayments, setCreditMemoPayments] = useState<{
    [key: string]: PaymentValue;
  }>({});

  const calculateMatchDetails = (
    type: "invoice" | "memo",
    paymentsObj: { [key: string]: { value: string; totalAmt: number } }
  ) => {
    return Object.entries(paymentsObj).map(([id, { value }]) => ({
      [type === "invoice" ? "invoiceId" : "creditMemoId"]: id,
      amount: parseFloat(value) || 0,
    }));
  };

  const calculateTotalMatchedAmount = (
    invoicePayments: { [key: string]: { value: string; totalAmt: number } },
    creditMemoPayments: { [key: string]: { value: string; totalAmt: number } }
  ) => {
    const invoiceMatch = calculateMatchDetails("invoice", invoicePayments);
    const memoMatch = calculateMatchDetails("memo", creditMemoPayments);
    let total;
    if (editTransaction && editTransaction?.amount >= 0) {
      total =
        invoiceMatch.reduce((sum, item) => sum + item.amount, 0) -
        memoMatch.reduce((sum, item) => sum + item.amount, 0);
    } else {
      total = memoMatch.reduce((sum, item) => sum + item.amount, 0);
    }

    return { total, invoiceMatch, memoMatch };
  };

  const getPaymentError = (
    value: string,
    totalAmt: number
  ): string | undefined => {
    const trimmed = value?.trim?.();
    if (trimmed === "") return "Payment is required";

    const num = Number(trimmed);
    if (isNaN(num)) return "Invalid payment amount";
    if (num <= 0) return "Payment must be positive.";
    if (num > Number(totalAmt)) return "Amount exceeds total.";
    return undefined;
  };

  const handlePaymentChange = (
    type: "invoice" | "memo",
    invoiceId: string,
    value: string,
    totalAmt: number
  ) => {
    const updatedPayment = { value, totalAmt };
    // Only set if value is less than or equal to totalAmt
    if (type === "memo") {
      const updated = {
        ...creditMemoPayments,
        [invoiceId]: updatedPayment,
      };
      setCreditMemoPayments(updated);
      const { total } = calculateTotalMatchedAmount(payments, updated);

      if (total > Math.abs(Number(editTransaction?.amount))) {
        setError("Total Payment amount cannot exceed Transaction amount.");
      } else if (total !== Math.abs(Number(editTransaction?.amount))) {
        setError("Total Payment amount is not same as Transaction amount.");
      } else {
        setError(null);
      }
    } else {
      const updated = {
        ...payments,
        [invoiceId]: updatedPayment,
      };
      setPayments(updated);

      const { total } = calculateTotalMatchedAmount(
        updated,
        creditMemoPayments
      );

      if (total > Number(editTransaction?.amount)) {
        setError("Total Payment amount cannot exceed Transaction amount.");
      } else if (total !== Number(editTransaction?.amount)) {
        setError("Total Payment amount is not same as Transaction amount.");
      } else {
        setError(null);
      }
    }
  };

  useEffect(() => {
    const isInvoiceSelected = Object.keys(rowSelection).length > 0;
    const isCreditMemoSelected = Object.keys(creditMemoRowSelection).length > 0;

    if (!isInvoiceSelected && !isCreditMemoSelected) return;

    const selectedInvoices = Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((index) => invoices[parseInt(index)]);

    const filteredA = Object.fromEntries(
      Object.entries(payments).filter(([id]) =>
        selectedInvoices.some((invoice) => invoice.invoiceId === id)
      )
    );
    setPayments(filteredA);

    const selectedCreditMemos = Object.keys(creditMemoRowSelection)
      .filter((key) => creditMemoRowSelection[key])
      .map((index) => creditMemos[parseInt(index)]);

    const filteredB = Object.fromEntries(
      Object.entries(creditMemoPayments).filter(([id]) =>
        selectedCreditMemos.some((memo) => memo.creditMemoId === id)
      )
    );
    setCreditMemoPayments(filteredB);

    const { total } = calculateTotalMatchedAmount(filteredA, filteredB);
    if (total > Number(editTransaction?.amount)) {
      setError("Total Payment amount cannot exceed Transaction amount.");
    } else if (total !== Number(editTransaction?.amount)) {
      setError("Total Payment amount is not same as Transaction amount.");
    } else {
      setError(null);
    }
  }, [creditMemoRowSelection, rowSelection]);

  function getMatchDetails(
    type: "invoice" | "memo",
    payments: { [key: string]: { value: string; totalAmt: number } }
  ) {
    if (type === "invoice") {
      return Object.entries(payments)
        .map(([id, { value, totalAmt }]) => {
          const amount = Number(value);
          if (isNaN(amount) || amount > Number(totalAmt)) return null;
          return { invoiceId: id, amount };
        })
        .filter(
          (item): item is { invoiceId: string; amount: number } => item !== null
        );
    } else {
      return Object.entries(payments)
        .map(([id, { value, totalAmt }]) => {
          const amount = Number(value);
          if (isNaN(amount) || amount > Number(totalAmt)) return null;
          return { creditMemoId: id, amount };
        })
        .filter(
          (item): item is { creditMemoId: string; amount: number } =>
            item !== null
        );
    }
  }

  const handleMatch = async () => {
    const selectedInvoiceIndexes = Object.keys(rowSelection || {});
    const selectedMemoIndexes = Object.keys(creditMemoRowSelection || {});

    const hasSelectedAny =
      selectedInvoiceIndexes.length > 0 || selectedMemoIndexes.length > 0;

    const selectedInvoiceUUIDs = selectedInvoiceIndexes
      .map((key) => invoices?.[Number(key)]?.invoiceId)
      .filter(Boolean);

    const selectedCreditMemoUUIDs = selectedMemoIndexes
      .map((key) => creditMemos?.[Number(key)]?.creditMemoId)
      .filter(Boolean);

    // Validate only selected rows
    const invalidInvoice = selectedInvoiceUUIDs.some((id) => {
      const p = payments[id];
      return getPaymentError(p?.value ?? "", p?.totalAmt ?? 0) !== undefined;
    });
    const invalidMemo = selectedCreditMemoUUIDs.some((id) => {
      const p = creditMemoPayments[id];
      return getPaymentError(p?.value ?? "", p?.totalAmt ?? 0) !== undefined;
    });

    if ((invalidInvoice || invalidMemo) && hasSelectedAny) {
      setError("Please fix errors in selected payment rows before matching.");
      return;
    }
    const matchDetails = getMatchDetails("invoice", payments);
    const matchCreditMemo = getMatchDetails("memo", creditMemoPayments);

    // Calculate the total amount being matched
    const totalMatchedAmount =
      matchDetails.reduce((sum, item) => sum + item!.amount, 0) -
      matchCreditMemo.reduce((sum, item) => sum + item!.amount, 0);

    // Check if it exceeds the transaction's total amount
    if (
      editTransaction &&
      totalMatchedAmount > Number(editTransaction.amount)
    ) {
      setError("Total Payment amount cannot exceed Transaction amount.");
      return;
    }

    if (
      editTransaction &&
      totalMatchedAmount !== Number(editTransaction.amount)
    ) {
      setError("Total Payment amount is not same as Transaction amount.");
      return;
    }
    setError(null);

    let payload: MatchTransactionPayload = {
      transactionId: editTransaction?.transactionId ?? "",
      paymentDate: editTransaction?.transactionDate?.toString() ?? "",
      matchDetails: matchDetails as { invoiceId: string; amount: number }[],
      matchCreditMemo: matchCreditMemo as {
        creditMemoId: string;
        amount: number;
      }[],
    };

    try {
      // Use fallback from state if no parameter provided
      setIsLoading({ ...isLoading, isMatchLoading: true });
      const response = await MatchTransactionApi(payload, businessId);
      if (response && response.success && response.data) {
        onSuccess?.(
          "POST",
          "matchTransaction",
          response.message || "Transaction matched successfully!"
        );
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      handleApiError(error, onError);
      // onError?.(new Error("An error occurred while matchiing transactions."));
    } finally {
      handleMatchTransaction(false, []);
      handleCloseDialog();
      await getCategoriesCount({});
      await fetchTransactions({});
      setIsLoading({ ...isLoading, isMatchLoading: false });
    }
  };

  interface Filters {
    startDate?: string;
    endDate?: string;
    term?: string;
    filterBy?: string;
  }
  const getInvoiceData = async (
    page: number,
    size: number,
    filterValues?: Filters
  ) => {
    setIsLoading({
      ...isLoading,
      isInvoiceLoading: true,
    });
    try {
      const queryParams = new URLSearchParams({
        pageSize: size.toString(),
      });

      queryParams.append("IsForMatch", "true");

      if (filterValues?.startDate) {
        queryParams.append("startDate", formatDate(filterValues.startDate));
      }
      if (filterValues?.endDate) {
        queryParams.append("endDate", formatDate(filterValues.endDate));
      }
      if (size) {
        queryParams.append("pageSize", size.toString());
      }
      if (filterValues?.filterBy) {
        queryParams.append("FilterBy", filterValues.filterBy);
      }
      if (filterValues?.term) {
        queryParams.append("Term", filterValues.term);
      }

      const response: ApiResponse<{
        totalPages: number;
        totalRecords: number;
        pageNumber: number;
        pageSize: number;
        items: Invoice[];
      }> = await apiRequest({
        url: `/receivableinvoice/${businessId}/List/${page}?${queryParams}`,
        method: "GET",
      });

      if (response.success) {
        const { totalPages, items, pageNumber, totalRecords } =
          response?.data || {};
        onSuccess?.(
          "GET",
          "Invoice",
          response?.message || "Data retrieved successfully!"
        );
        setInvoices(items || []);
        setPagination({
          ...pagination,
          pageSize: size,
          pageNumber: pageNumber ?? 1,
          totalPages: totalPages ?? 1,
          totalRecords: totalRecords ?? 0,
        });
      } else {
        handleApiError(response, onError);
      }
    } catch (err) {
      onError?.(new Error("An error occurred while fetching Invoice data."));
    } finally {
      setIsLoading({
        ...isLoading,
        isInvoiceLoading: false,
      });
      setRowSelection({});
      setPayments({});
      setError(null);
    }
  };

  const getCreditMemoData = async (
    page: number,
    size: number,
    filterValues?: Filters
  ) => {
    setIsLoading({ ...isLoading, isMemoLoading: true });
    try {
      const queryParams = new URLSearchParams({
        pageSize: size.toString(),
      });

      queryParams.append("IsForMatch", "true");
      if (filterValues?.startDate) {
        queryParams.append("startDate", formatDate(filterValues.startDate));
      }
      if (filterValues?.endDate) {
        queryParams.append("endDate", formatDate(filterValues.endDate));
      }
      if (size) {
        queryParams.append("pageSize", size.toString());
      }
      if (filterValues?.filterBy) {
        queryParams.append("FilterBy", filterValues.filterBy);
      }
      if (filterValues?.term) {
        queryParams.append("Term", filterValues.term);
      }

      const response: ApiResponse<{
        totalPages: number;
        totalRecords: number;
        pageNumber: number;
        pageSize: number;
        items: CreditMemo[];
      }> = await apiRequest({
        url: `/creditmemo/${businessId}/List/${page}?${queryParams}`,
        method: "GET",
      });

      if (response.success) {
        const { totalPages, items, pageNumber, totalRecords } =
          response?.data || {};
        onSuccess?.(
          "GET",
          "CreditMemo",
          response?.message || "Data retrieved successfully!"
        );
        setCreditMemos(items || []);
        setCreditMemoPagiation({
          ...pagination,
          pageSize: size,
          pageNumber: pageNumber ?? 1,
          totalPages: totalPages ?? 1,
          totalRecords: totalRecords ?? 0,
        });
      } else {
        handleApiError(response, onError);
      }
    } catch (err) {
      onError?.(
        new Error("An error occurred while Matching Transaction data.")
      );
    } finally {
      setIsLoading({ ...isLoading, isMemoLoading: false });
      setCreditMemoRowSelection({});
      setCreditMemoPayments({});
      setError(null);
    }
  };

  // while pagination reftech data
  useEffect(() => {
    if (
      modalStates.isMatchTransactionModalOpen &&
      editTransaction &&
      editTransaction?.amount >= 0
    ) {
      getInvoiceData(
        pagination.pageNumber,
        pagination.pageSize,
        transactionfilters as any
      );
    }
  }, [pagination.pageNumber, pagination.pageSize]);

  useEffect(() => {
    if (modalStates.isMatchTransactionModalOpen) {
      getCreditMemoData(
        creditMemoPagiation.pageNumber,
        creditMemoPagiation.pageSize,
        memofilters as any
      );
    }
  }, [creditMemoPagiation.pageNumber, creditMemoPagiation.pageSize]);

  // Handle page size change
  const handlePageSizeChange = (type: "memo" | "invoice", size: number) => {
    if (type === "memo") {
      setCreditMemoPagiation({
        ...creditMemoPagiation,
        pageSize: size,
        pageNumber: 1,
      });
    } else {
      setPagination({ ...pagination, pageSize: size, pageNumber: 1 });
    }
  };

  // while filters refetch data
  useEffect(() => {
    if (
      modalStates.isMatchTransactionModalOpen &&
      editTransaction &&
      editTransaction?.amount >= 0
    ) {
      // Only fetch if filters are initialized (optional: add your own checks if needed)
      getInvoiceData(1, pagination.pageSize, transactionfilters as any);
    }
  }, [transactionfilters]);

  useEffect(() => {
    if (modalStates.isMatchTransactionModalOpen) {
      // Only fetch if filters are initialized (optional: add your own checks if needed)
      getCreditMemoData(1, pagination.pageSize, memofilters as any);
    }
  }, [memofilters]);

  // Handle page number change
  const handlePageChange = (type: "invoice" | "memo", page: number) => {
    if (type === "memo") {
      setCreditMemoPagiation({ ...creditMemoPagiation, pageNumber: page });
    } else {
      setPagination({ ...pagination, pageNumber: page });
    }
  };

  const fetchFilterByList = async () => {
    try {
      const response: ApiResponse<any> = await apiRequest({
        url: `/receivableinvoice/GetFilterByList`,
        method: "GET",
      });
      if (response.success && response.data) {
        onSuccess?.(
          "GET",
          "fetchFilterByList",
          response?.message || "Data retrieved successfully!"
        );
        const options = response?.data?.map(
          (status: InvoiceFilterByInterface) => ({
            label: status.codeName,
            value: status.masterId.toString(),
          })
        );
        setFilterByOptions((prev) => ({
          ...prev,
          invoiceOptions: options || [],
        }));
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while fetching Invoice Status."));
    }
  };

  const fetchCreditMemoFilterByList = async () => {
    try {
      const response: ApiResponse<any> = await apiRequest({
        url: `/creditmemo/GetFilterByList`,
        method: "GET",
      });
      if (response.success && response.data) {
        onSuccess?.(
          "GET",
          "fetchFilterByList",
          response?.message || "Data retrieved successfully!"
        );
        const options = response?.data?.map(
          (status: InvoiceFilterByInterface) => ({
            label: status.codeName,
            value: status.masterId.toString(),
          })
        );
        setFilterByOptions((prev) => ({
          ...prev,
          memoOptions: options || [],
        }));
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while fetching Memo Status."));
    }
  };

  const handleFilterChange = (
    type: "invoice" | "credit",
    field: string,
    value: any
  ) => {
    if (type == "invoice") {
      setTransactionFilters((prev) => ({ ...prev, [field]: value }));
    } else {
      setMemoFilters((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleCloseDialog = () => {
    setFilterByOptions({ invoiceOptions: [], memoOptions: [] });
    setCreditMemoRowSelection({});
    setRowSelection({});
    setPayments({});
    setCreditMemoPayments({});
    setInvoices([]);
    setCreditMemos([]);

    setTransactionFilters({
      startDate: null,
      endDate: null,
      term: "",
      filterBy: "",
    });
    setMemoFilters({
      startDate: null,
      endDate: null,
      term: "",
      filterBy: "",
    });
  };

  useEffect(() => {
    if (modalStates.isMatchTransactionModalOpen) {
      if (editTransaction && editTransaction?.amount >= 0) {
        fetchFilterByList();
        setTransactionFilters({
          startDate: getMonthStart(),
          endDate: getMonthEnd(),
          term: "",
          filterBy: "",
        });
      }

      fetchCreditMemoFilterByList();
      setMemoFilters({
        startDate: getMonthStart(),
        endDate: getMonthEnd(),
        term: "",
        filterBy: "",
      });
    }
  }, [modalStates]);

  return (
    <CreditMemoContext.Provider
      value={{
        invoices,
        isLoading,
        pagination,
        handlePageChange,
        handlePageSizeChange,
        filterByOptions,
        transactionfilters,
        setTransactionFilters,
        memofilters,
        handleFilterChange,
        fetchFilterByList,
        rowSelection,
        setRowSelection,
        payments,
        handlePaymentChange,
        creditMemos,
        creditMemoPagiation,
        creditMemoRowSelection,
        setCreditMemoRowSelection,
        creditMemoPayments,
        handleMatch,
        error,
        handleCloseDialog,
        getPaymentError,
      }}
    >
      {children}
    </CreditMemoContext.Provider>
  );
};
